import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, switchMap, throwError } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AccessDeniedPayload } from './auth.types';
import { AuthService } from './auth.service';
import { resolveSessionEndReason } from './resolve-session-end-reason';
import { SubscriptionGateService } from './subscription-gate.service';

/**
 * - Attaches Authorization: Bearer <token> to every API request when we have one.
 * - On 401, wipes local auth and bounces to /login.
 * - On 402 (subscription required), surfaces the gate service so the UI
 *   can show the "Tu suscripcion expiro" dialog without losing the URL the
 *   user was trying to reach.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const gate = inject(SubscriptionGateService);

  const isApiRequest = req.url.startsWith(environment.apiBaseUrl);
  const token = auth.token();

  const authedReq =
    isApiRequest && token
      ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : req;

  return next(authedReq).pipe(
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse && isApiRequest) {
        if (err.status === 401) {
          const reason = resolveSessionEndReason(auth, err);
          return from(
            auth.handleSessionEnded(reason, { returnUrl: router.url }),
          ).pipe(switchMap(() => throwError(() => err)));
        }
        if (err.status === 402) {
          const payload = err.error as AccessDeniedPayload | undefined;
          gate.notifyBlocked(payload);
        }
      }
      return throwError(() => err);
    }),
  );
};
